<template>
  <span :id="'seat-' + info.number" class="d-inline-block" tabindex="0">
    <button
      v-b-hover="handleHover"
      class="map-block seat"
      :disabled="availability !== 'available'"
      @click="() => handleSeatSelection()"
      role="button"
    >
      <div :class="'seat-info block-info seat-component ' + seatClass">
        <span v-if="isSelected" class="seat-info-initials">{{
          displayPassengerSelected(passenger)
        }}</span>
        <span v-else-if="isBlocked" v-html="svgSeatBlocked()"></span>
        <span
          v-else-if="
            !isSelected && isAvailable && isSuitableForInfant && (!isHovered || price === 0)
          "
          class="seat-info-price"
          v-html="svgSeatBestForInfant(isPremium, 20)"
        ></span>
        <span
          v-else-if="
            !isSelected && isAvailable && price !== 0 && (!isSuitableForInfant || isHovered)
          "
          class="seat-info-price"
        >
          <span>{{ formattedPrice() }}</span>
          <span class="d-none d-xl-inline d-lg-inline">{{ formatCurrency(currency) }}</span>
        </span>
      </div>
    </button>
    <b-popover
      v-if="info.number"
      :target="'seat-' + info.number"
      variant="ghost"
      triggers="hover"
      placement="top"
    >
      <div class="border-bottom-2 seat-popover">
        <div :class="isAvailable && isPremium ? 'text-premium' : 'text-primary'">
          Posto {{ info.number }}
        </div>
        <div class="seat-popover-basic-info">
          <span v-if="isSelected">{{ passengerName }}</span
          ><span v-else>{{ getDisplayAvailability(availability).toUpperCase() }}</span
          ><span v-if="price !== 0">{{ formattedPrice(false) }}</span>
        </div>
      </div>
      <div v-if="isPremium" class="seat-characteristics-info">
        <span>{{
          shownCharacteristics.map((ch) => userKnowledgeSeatCharacteristics[ch]).join(', ')
        }}</span>
      </div>
      <div v-if="isPartiallyAvailable" class="seat-characteristics-info">
        <span>Questo posto non è disponibile per tutti i passeggeri indicati</span>
      </div>
    </b-popover>
  </span>
</template>

<script>
import {
  userKnowledgeSeatCharacteristics,
  premiumSeatCharacteristics,
  SEAT_SUITABLE_FOR_INFANT,
} from '@/resources/seat-map';
import { svgSeatBlocked, svgSeatBestForInfant } from '@/components/svg/seat-map';
import { formatPrice } from '../../../utilities';
import { getDisplayAvailability } from '../../../utilities/seat-map';

export default {
  name: 'seat',
  props: {
    info: Object,
    passenger: Object,
    handleSeatSelection: Function,
  },
  data() {
    return {
      isHovered: false,
      userKnowledgeSeatCharacteristics,
      premiumSeatCharacteristics,
      svgSeatBlocked,
      svgSeatBestForInfant,
    };
  },
  computed: {
    currentTravelerChoosing() {
      return this.info.currentTravelerChoosing;
    },
    seatClass() {
      if (this.isSelected) {
        return this.isPremium ? 'seat-premium-selected' : 'seat-selected';
      }

      if (this.isAvailable) {
        if (this.isPremium) {
          return 'seat-premium';
        }
        if (this.isPartiallyAvailable) {
          return 'seat-partially-available';
        }
      }
      return 'seat-' + this.availability;
    },
    characteristicsCodes: function () {
      if (!this.info) {
        return [];
      }
      return this.info.characteristicsCodes || [];
    },
    travelerPricing: function () {
      if (!this.info) {
        return {};
      }
      if (this.info.currentTravelerPricing) {
        return this.info.currentTravelerPricing;
      }
      const travelerPricing = this.info.travelerPricing;
      if (travelerPricing && travelerPricing.length) {
        if (!this.currentTravelerChoosing) {
          return this.info.travelerPricing[0];
        }
        const index = travelerPricing.findIndex(
          (pricing) => pricing.travelerId === this.currentTravelerChoosing
        );
        return this.info.travelerPricing[Math.max(0, index)];
      }
      return {};
    },
    availability: function () {
      const { seatAvailabilityStatus = '' } = this.travelerPricing;
      return String(seatAvailabilityStatus).toLowerCase();
    },
    price: function () {
      const { price } = this.travelerPricing;
      if (!price || !price.grandTotal) {
        return 0;
      }
      return Number(price.grandTotal);
    },
    currency: function () {
      const { price = {} } = this.travelerPricing;
      return price.currency || 'EUR';
    },
    isPartiallyAvailable: function () {
      return this.info.isPartiallyAvailable;
    },
    isSelected: function () {
      return !!this.passenger;
    },
    isSuitableForInfant: function () {
      return this.characteristicsCodes.includes(SEAT_SUITABLE_FOR_INFANT);
    },
    isAvailable: function () {
      return this.availability === 'available';
    },
    isBlocked: function () {
      return this.availability === 'blocked';
    },
    isPremium: function () {
      return this.characteristicsCodes.some((ch) => this.premiumSeatCharacteristics[ch]);
    },
    passengerName: function () {
      if (!this.passenger) {
        return '';
      }
      return this.passenger.firstname + ' ' + this.passenger.lastname;
    },
    shownCharacteristics: function () {
      return this.characteristicsCodes.filter((ch) => this.userKnowledgeSeatCharacteristics[ch]);
    },
  },
  methods: {
    formatPrice,
    getDisplayAvailability,
    displayPassengerSelected(passenger) {
      if (!passenger) {
        return '';
      }
      return 'P' + passenger.id;
    },
    formattedPrice: function (display = true) {
      return display ? Math.ceil(this.price) : this.formatPrice(this.price);
    },
    formatCurrency(verbose = '') {
      switch (verbose.toUpperCase()) {
        case 'EUR':
          return '€';
        default:
          return '€';
      }
    },
    handleHover(hovered) {
      this.isHovered = hovered;
    },
  },
};
</script>

<style lang="scss">
.seat {
  padding: 0;
  background-color: $light-gray-blue;
  border-color: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.seat-component {
  font-weight: bold;
  color: $available-seat-border;
  border: 2px solid $available-seat-border;
}

.seat-occupied,
.seat-blocked {
  opacity: 30%;
}
.seat-selected {
  background-color: $light-blue;
  color: $primary;
  border: 2px solid $primary;
}

.seat-premium {
  color: $premium-seat-border;
  border: 2px solid $premium-seat-border;
  background-color: $premium-seat-background;
}

.seat-premium-selected {
  color: $selected-premium-seat-border;
  border: 2px solid $selected-premium-seat-border;
  background-color: $selected-premium-seat-background;
}

.seat-partially-available {
  color: $partially-available-seat-border;
  border: 2px solid $partially-available-seat-border;
  background-color: $partially-available-seat-background;
}

.seat-info {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  max-width: 100%;
  min-width: 100%;
  max-height: 100%;
  min-height: 100%;
}

.text-premium {
  color: $premium-seat-border;
}

.seat-popover {
  min-width: 224px;
}

.seat-popover-basic-info {
  display: flex;
  justify-content: space-between;
  gap: 10%;
}
</style>
